import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { formatNumber } from "../../helpers/formatter";
import { periodWindowLabelTitle } from "../../helpers/periodWindowLabel";
import { resolveCardWindow } from "../../helpers/cardWindow";
import MountainClimb, { GREEN as MT_GREEN, RED as MT_RED, RidgeDefs } from "../BeatTheYeti/climb";
import useDeptMetrics from "./data";
import CellPopover from "./CellPopover";
import CardBusy from "../CardScope/CardBusy";
import "./dept.css";

/**
 * All Department Metrics — every workspace's own metrics on one board, each
 * cell green or red on whether that team is beating the Yeti right now.
 *
 * The card is a scanning tool, not a report: the colour is the message and the
 * number is the evidence, so the tint reads from across a room and the detail
 * waits under the pointer. A row is a workspace and its cells are ITS OWN
 * metrics — departments rarely track the same things, so the columns are
 * positions rather than shared headings.
 *
 * Every figure is computed in ./data.js. Nothing here decides arithmetic, and
 * nothing here decides what counts as beating him.
 */

/* The gate palette the rest of the board already uses — a Gate card tints its
   frame by status and these cells do the same, so they read as siblings
   (GATE_STYLE in components/BeatTheYeti). */
const GOOD = { ink: "#12a150", soft: "rgba(18, 161, 80, 0.1)", border: "rgba(18, 161, 80, 0.4)" };
const BAD = { ink: "#d92d20", soft: "rgba(240, 68, 56, 0.1)", border: "rgba(240, 68, 56, 0.4)" };
const FLAT = { ink: null, soft: null, border: null };

/* The bar view's fills — the Payroll vs Revenue rail's green, but the behind
   state is a straight red: that rail runs red into orange, which on a board of
   many bars reads as a third status. Red here means exactly one thing. */
const BAR_FILL = {
  good: "linear-gradient(90deg,#22c55e,#12a150)",
  bad: "linear-gradient(90deg,#F04438,#d92d20)",
  flat: "#d0d5dd",
};

/* The bar's fill and the Yeti's notch, as percentages of the track. A metric
   with nothing logged yet draws an EMPTY bar — the climb clamps its dots off
   the ends so they stay visible, but a bar at 6% would claim progress that
   isn't there. */
const barPct = (frac, amt) => (Number(amt) > 0 ? Math.round(Math.max(0, Math.min(1, frac)) * 100) : 0);

const toneOf = (beating) => {
  if (beating === true) return GOOD;
  if (beating === false) return BAD;
  return FLAT;
};

/* Beating is three-state — yes, no, and "there is no pace on this metric" —
   so every read of it needs all three branches. Named once here rather than
   inlined at each use, both because a nested ternary is unreadable and
   because the third state is the one easy to forget. */
const statusKey = (beating) => {
  if (beating === true) return "good";
  if (beating === false) return "bad";
  return "flat";
};
const statusText = (beating) => {
  if (beating === true) return "beating the Yeti";
  if (beating === false) return "behind the Yeti";
  return "no pace set";
};
/* All green or all red is worth colouring; anything in between is just a
   count and stays in the card's own muted ink. */
const countTone = (won, total) => {
  if (!total) return undefined;
  if (won === total) return GOOD.ink;
  if (won === 0) return BAD.ink;
  return undefined;
};


const DeptMetrics = ({ settings, title, boardWindow, boardCustom }) => {
  /* "Match the dashboard" (default) or a range pinned in Setup — the same rule
     every other cross-workspace card follows (helpers/cardWindow.js). */
  const { window: bucket, custom: resolvedCustom } = resolveCardWindow(settings, {
    window: boardWindow,
    custom: boardCustom,
  });
  const custom = bucket === "custom" ? (resolvedCustom || boardCustom) : null;

  const data = useDeptMetrics(settings, bucket, custom);

  /* "Climb" (default) draws Beat the Yeti's mountain in each tile; "bar" draws
     a plain progress bar instead — green while beating, red while behind. */
  const barView = settings?.tileStyle === "bar";
  const hide = settings?.hide || {};
  const shows = (key) => !hide[key];
  const cardTitle = title || settings?.title || "All Department Metrics";
  const periodLabel = periodWindowLabelTitle(bucket, custom);

  /* The hover detail stays mounted a beat after the pointer leaves so its exit
     can play instead of the card just vanishing; `hoverOn` is the actual on/off
     switch. Anchored by a rect snapshotted on enter, so a scroll while it is
     open closes it outright rather than leaving it pointing at nothing. */
  const [hover, setHover] = useState(null);
  const [hoverOn, setHoverOn] = useState(false);
  const hideTimer = useRef(null);

  const cellEnter = (row, cell, e) => {
    clearTimeout(hideTimer.current);
    setHover({ row, cell, anchor: e.currentTarget.getBoundingClientRect() });
    setHoverOn(true);
  };
  const cellLeave = () => {
    setHoverOn(false);
    // Outlives .dm-pop's own 200ms exit (dept.css) so the fade finishes.
    hideTimer.current = setTimeout(() => setHover(null), 220);
  };

  useEffect(() => {
    if (!hover) return undefined;
    const close = () => { setHoverOn(false); setHover(null); };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [hover]);
  useEffect(() => () => clearTimeout(hideTimer.current), []);

  const fmt = (cell) => {
    const n = Number(cell.playerAmt) || 0;
    // Percent amounts are stored as fractions (0.4314 = 43.14%), so rounding
    // the raw fraction would collapse a real rate to 0% or 100%.
    const rounded = cell.kind === "%" ? Math.round(n * 100) / 100 : Math.round(n);
    return formatNumber(rounded || 0, cell.kind);
  };

  const body = () => {
    if (data.loading) return null;
    if (!data.rows.length) {
      return (
        <div className="dm-empty">
          <b>No departments yet</b>
          Pick the workspaces this board should track in Edit component → Setup.
        </div>
      );
    }
    return (
      <div className="dm-scroll">
        <div className="dm-grid" style={{ "--dm-cols": String(data.cols) }}>
          {data.rows.map((row) => (
            <div className={`dm-row${row.depth ? " sub" : ""}`} key={row.key}>
              <div className="dm-dept">
                <span className="dm-dept-t">{row.name}</span>
                {shows("rowStatus") && row.scored > 0 && (
                  <span className="dm-dept-s" style={{ color: countTone(row.beating, row.scored) }}>
                    {row.beating}/{row.scored} beating
                  </span>
                )}
              </div>
              {row.empty ? (
                /* A grouping workspace tracks nothing of its own. Saying so
                   beats a row of empty cells that looks like a loading bug. */
                <div className="dm-none">No metrics on this workspace</div>
              ) : (
                <div className="dm-cells">
                  {row.cells.map((cell) => {
                    /* The delta's colour and arrow follow the AMOUNT gap, not
                       the points race — the same split Beat the Yeti makes, so
                       a negative delta still reads red on a metric whose points
                       say winning. Judged on the rounded figure the tile
                       prints, so a deficit shown as "0" can't carry an arrow
                       that contradicts it. */
                    const rounded = cell.kind === "%"
                      ? Math.round(cell.diff * 100) / 100
                      : Math.round(cell.diff);
                    const ahead = cell.lower ? rounded <= 0 : rounded >= 0;
                    return (
                      <button
                        type="button"
                        key={cell.kpiId}
                        className={`dm-cell gmbw-mt-tile ${statusKey(cell.beating)}${barView ? " bar" : ""}`}
                        onMouseEnter={(e) => cellEnter(row, cell, e)}
                        onMouseLeave={cellLeave}
                        onFocus={(e) => cellEnter(row, cell, e)}
                        onBlur={cellLeave}
                        aria-label={`${row.name} — ${cell.name}: ${fmt(cell)}, ${statusText(cell.beating)}`}
                      >
                        {barView ? (
                          <div className="dm-bar-in">
                            <div className="gmbw-mt-name">{cell.name}</div>
                            <div className="dm-bar-line">
                              {shows("value") && <span className="gmbw-mt-val">{fmt(cell)}</span>}
                              {shows("delta") && cell.beating !== null && (
                                <span className="gmbw-mt-delta" style={{ color: ahead ? MT_GREEN : MT_RED }}>
                                  {rounded > 0 ? "+" : ""}
                                  {formatNumber(rounded || 0, cell.kind)} {ahead ? "↑" : "↓"}
                                </span>
                              )}
                            </div>
                            <span className="dm-rail" aria-hidden="true">
                              <span
                                className="dm-rail-fill"
                                style={{
                                  transform: `scaleX(${barPct(cell.playerFrac, cell.playerAmt) / 100})`,
                                  background: BAR_FILL[statusKey(cell.beating)],
                                }}
                              />
                              {cell.beating !== null && Number(cell.yetiAmt) > 0 && (
                                <span className="dm-rail-yeti" style={{ left: `${barPct(cell.yetiFrac, cell.yetiAmt)}%` }} />
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            <MountainClimb
                              playerFrac={cell.playerFrac}
                              yetiFrac={cell.yetiFrac}
                              winning={cell.beating !== false}
                              strokeWidth={3.2}
                            />
                            <div className="gmbw-mt-info">
                              <div className="gmbw-mt-name">{cell.name}</div>
                              {shows("value") && <div className="gmbw-mt-val">{fmt(cell)}</div>}
                              {shows("delta") && cell.beating !== null && (
                                <div className="gmbw-mt-delta" style={{ color: ahead ? MT_GREEN : MT_RED }}>
                                  {rounded > 0 ? "+" : ""}
                                  {formatNumber(rounded || 0, cell.kind)} {ahead ? "↑" : "↓"}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </button>
                    );
                  })}
                  {/* Placeholders keep every row on the same column grid: a
                      team with three metrics beside one with five must not
                      stretch its cells to fill the gap, or the two rows stop
                      lining up and the grid stops being scannable. */}
                  {Array.from({ length: Math.max(0, data.cols - row.cells.length) }, (_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <span className="dm-cell ghost" key={`g${i}`} aria-hidden="true" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`gmbw-sales-card dm${data.refreshing ? " dm-refreshing" : ""}`}>
      <CardBusy active={data.loading || data.refreshing} cover={data.loading} label={data.loading ? "Loading metrics…" : "Updating for date range…"} />
      <div className="gmbw-sales-head">
        <span className="gmbw-sales-title">{cardTitle}</span>
        <span className="gmbw-sales-period">{periodLabel}</span>
      </div>
      <div className="gmbw-sales-body">
        {/* The ridge gradients the climbs reference. Global ids by necessity
            (SVG url(#…) has no scope) — see climb.jsx. */}
        <RidgeDefs />
        {shows("summary") && data.scored > 0 && (
          <div className="dm-sum">
            <span className="dm-sum-v" style={{ color: countTone(data.beating, data.scored) }}>
              {data.beating} of {data.scored}
            </span>
            <span className="dm-sum-k">metrics beating the Yeti across {data.rows.length} department{data.rows.length === 1 ? "" : "s"}</span>
            <span className="dm-legend">
              <i className="dm-legend-d good" />Beating
              <i className="dm-legend-d bad" />Behind
            </span>
          </div>
        )}
        {body()}
      </div>
      {hover && (
        <CellPopover
          key={`${hover.row.key}:${hover.cell.kpiId}`}
          cell={hover.cell}
          workspace={hover.row.name}
          tone={toneOf(hover.cell.beating)}
          anchor={hover.anchor}
          active={hoverOn}
        />
      )}
    </div>
  );
};

DeptMetrics.propTypes = {
  /* The card's own settings blob — the same free-form shape every widget stores. */
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
  title: PropTypes.string,
  boardWindow: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  boardCustom: PropTypes.object,
};

DeptMetrics.defaultProps = {
  settings: {},
  title: undefined,
  boardWindow: null,
  boardCustom: null,
};

export default DeptMetrics;
